import React, { useState, useEffect, Fragment, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Divider, makeStyles, Box, Typography } from '@material-ui/core';
import { useRouteMatch } from 'react-router';

import { useProjectApi } from 'src/services';
import { AnalyticCard } from 'src/scenes/Project/components';
import { ConcernSummary, LoadingBox, GeospatialAnalytic } from 'src/scenes/Project/scenes/Dashboard/components';
import { useFetchStatus } from 'src/hooks';
import { getLeafletOptionsFn } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { ContentContainer, TitleContainer } from 'src/components';
import { WebContext } from 'src/scenes/web-context';
import { processComponentConcerns, componentConfig } from 'src/utils/natureStateIndicatorsHelper';


const useStyles = makeStyles(theme => ({
  map: {
    borderBottomLeftRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
  },
  divider: {
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  analyticCardPaper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  concernLabel: {
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.error.main,
    backgroundColor: '#fcdada',
    height: theme.spacing(3.5),
    minWidth: theme.spacing(18),
    maxWidth: theme.spacing(18),
    fontWeight: 500,
    marginLeft: theme.spacing(3),
  },
  concernTitle: {
    display: 'flex',
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
  },
  indicatorLabel: {
    fontStyle: 'italic',
    display: 'inline',
  },
  subtitle: {
    fontWeight: 500,
    textTransform: 'uppercase',
    color: theme.palette.common.gray600,
    lineHeight: 1,
  },
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
}));

const landUseAnalyticsTypes = [ 'landcover', 'natural-vegetation-proportion' ];

const LandContainer = ({ interestArea, project }) => {
  const match = useRouteMatch();
  const classes = useStyles();
  const { currentUser: { token } } = useContext(WebContext);
  const { isLoading: isLoadingInterestArea } = interestArea.fetchStatus;

  const [ geospatialAnalytic, setGeospatialAnalytic ] = useState([]);
  const [ geospatialOptions, setGeospatialOptions ] = useState({
    spectralIndicesOptions: [],
    landUseOptions: [],
  });

  const projectApi = useProjectApi();

  const [ concerns, setConcerns ] = useState([]);

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const componentId = match.url.split('/').filter(Boolean).pop();

  const { handleConfirm: fetchConcerns, isLoading: isLoadingConcerns, isSuccess: isSuccessConcerns } = useFetchStatus({
    confirmFn: async () => {
      const indicators = await projectApi.getNatureStateIndicators({ projectId });
      const processedConcerns = processComponentConcerns(indicators, 'land', 2024);
      const concerns = processedConcerns.map(concern => {
        const indicatorConfig = componentConfig.land.indicators[concern.fieldKey];
        return {
          ...concern,
          tooltip: indicatorConfig.tooltip,
          url: indicatorConfig.url,
          ReasonComponent: () =>
            !isSite &&
              <>
                basado en el
                <Typography className={classes.indicatorLabel} variant="body2" component="span">
                  &nbsp;{indicatorConfig.description}&nbsp;
                </Typography>
                del indicador
                <Typography className={classes.indicatorLabel} variant="body2" component="span">
                  &nbsp;{indicatorConfig.fullName} ({indicatorConfig.shortName})
                </Typography>
              </>,
        };
      });

      setConcerns(concerns);
    },
  });

  useEffect(() => {
    fetchConcerns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    handleConfirm: fetchGeospatialAnalytics,
    isLoading: isLoadingGeospatialAnalytics,
    isSuccess: isSuccessGeospatialAnalytics,
  } = useFetchStatus({
    confirmFn: async () => {
      const [ geospatialAnalytic, geospatialOptions ] = await Promise.all([
        projectApi.getGeospatialAnalytics({ projectId, componentId }),
        projectApi.getGeospatialOptions({ projectId, componentId }),
      ]);
      setGeospatialAnalytic(geospatialAnalytic);
      setGeospatialOptions(geospatialOptions);
    },
  });

  useEffect(() => {
    fetchGeospatialAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasInterestArea = interestArea.data.length > 0;

  const landUseAnalytics = useMemo(() =>
    geospatialAnalytic.find(g => landUseAnalyticsTypes.includes(g.geoInfoTypeId)), [ geospatialAnalytic ]);
  const indexAnalytics = useMemo(() =>
    geospatialAnalytic.find(g => !landUseAnalyticsTypes.includes(g.geoInfoTypeId)), [ geospatialAnalytic ]);

  return (
    <Grid container>
      <TitleContainer
        maxWidth='xl'
        project={project}
        breadcrumbs={[ 'projects', 'project', 'sites', 'site', { name: 'Tierra' } ]}
      >
        Estado de la naturaleza - Tierra
      </TitleContainer>
      <ContentContainer maxWidth='xl'>
        <Grid container spacing={1} item>
          {(!hasInterestArea && !isLoadingInterestArea) &&
            <Grid container spacing={1} item>
              <Grid item xs={12}>
                <Box my={2}>
                  <Typography variant="h6" color="textSecondary" align="center">
                    No se ha definido un área de interés para este proyecto.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Para visualizar las métricas del proyecto, primero debe definir un área de interés.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Puede hacerlo desde la sección de <strong>Áreas de interés</strong> en el menú del proyecto.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          }

          <Grid item xs={12}>
            <AnalyticCard title='Indicadores'>
              <Divider />
              <Box my={0.5} />
              {isLoadingConcerns && <LoadingBox label='indicadores de tierra' />}
              {isSuccessConcerns &&
                <Box display='flex' flexDirection='column' alignItems='center'>
                  {concerns.map(({ name, level, value, isPartial, tooltip, url, ReasonComponent }, index) =>
                    <Fragment key={`concern-${index}`}>
                      <ConcernSummary title={name} level={level} value={value} isPartial={isPartial} tooltip={tooltip} url={url}>
                        Nivel de preocupación <strong>{level.name}</strong> para {name} <ReasonComponent />
                      </ConcernSummary>
                      {(concerns.length - 1) !== index && <Divider className={classes.divider} />}
                    </Fragment>,
                  )}

                </Box>
              }
            </AnalyticCard>
          </Grid>

          <GeospatialAnalytic
            title="Índices espectrales"
            analytic={indexAnalytics}
            interestArea={interestArea.data}
            getLeafletOptions={bounds => getLeafletOptionsFn({ token, bounds })}
            geospatialTypesOptions={geospatialOptions.spectralIndicesOptions}
            fetchStatus={{ isLoading: isLoadingGeospatialAnalytics, isSuccess: isSuccessGeospatialAnalytics }}
          />

          <GeospatialAnalytic
            id="landcover"
            title="Uso de suelo"
            analytic={landUseAnalytics}
            interestArea={interestArea.data}
            getLeafletOptions={bounds => getLeafletOptionsFn({ token, bounds })}
            geospatialTypesOptions={geospatialOptions.landUseOptions}
            fetchStatus={{ isLoading: isLoadingGeospatialAnalytics, isSuccess: isSuccessGeospatialAnalytics }}
          />

          {/* <Grid item xs={12}>
          <MonitoringPlanAnalytics />
        </Grid> */}

        </Grid>
      </ContentContainer>
    </Grid>
  );
};

LandContainer.propTypes = {
  interestArea: PropTypes.shape({
    data: PropTypes.array.isRequired,
    fetchStatus: PropTypes.object,
  }).isRequired,
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export { LandContainer };
